import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const DateTimeSelector = ({ provider, selectedDate, selectedTime, onDateChange, onTimeChange, duration = 60 }) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existingBookings, setExistingBookings] = useState([]);

  // Helper function to convert new weeklyAvailability format to old format
  const convertWeeklyToRecurring = (weeklyAvailability) => {
    const recurringSlots = [];
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    dayMap.forEach((dayKey, dayOfWeek) => {
      const dayData = weeklyAvailability[dayKey];
      if (dayData && dayData.available) {
        dayData.timeSlots.forEach(slot => {
          recurringSlots.push({
            id: slot.id,
            dayOfWeek: dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            isActive: true
          });
        });
      }
    });
    
    return recurringSlots;
  };
  
  // Generate calendar days (next 30 days)
  const generateCalendarDays = () => {
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Use local timezone instead of UTC to avoid timezone conversion issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      const dayOfWeek = date.getDay();
      
      console.log(`Day ${i}: ${dateString}, dayOfWeek: ${dayOfWeek} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek]})`);
      
      days.push({
        date: dateString,
        dayOfWeek: dayOfWeek,
        display: date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        isToday: i === 0
      });
    }
    return days;
  };

  const [calendarDays] = useState(generateCalendarDays());

  // Fetch existing bookings for the provider
  const fetchExistingBookings = async (date) => {
    try {
      const sessionsQuery = query(
        collection(db, 'sessions'),
        where('providerId', '==', provider.id),
        where('scheduledDate', '==', date),
        where('status', 'in', ['scheduled', 'confirmed', 'in_progress'])
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const bookings = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return bookings;
    } catch (error) {
      console.error('Error fetching existing bookings:', error);
      return [];
    }
  };

  // Generate 15-minute time slots
  const generateTimeSlots = (startTime, endTime) => {
    const slots = [];
    const start = new Date(`2024-01-01T${startTime}:00`);
    const end = new Date(`2024-01-01T${endTime}:00`);
    
    while (start < end) {
      const timeString = start.toTimeString().substring(0, 5);
      slots.push(timeString);
      start.setMinutes(start.getMinutes() + 15);
    }
    
    return slots;
  };

  // Check if provider is available on a given date
  const getProviderAvailability = (date, dayOfWeek) => {
    console.log(`getProviderAvailability called with date: ${date}, dayOfWeek: ${dayOfWeek}`);
    const availabilitySlots = [];

    // Check one-off availability for this specific date (active one-off overrides recurring)
    let hasActiveOneOffForThisDate = false;
    if (provider.oneOffAvailability) {
      console.log('Checking one-off availability:', provider.oneOffAvailability);
      const activeOneOffSlots = provider.oneOffAvailability.filter(slot => {
        const isActive = slot.isActive;
        const dateMatches = slot.date === date;
        console.log(`One-off slot check: isActive=${isActive}, dateMatches=${dateMatches} (slot.date=${slot.date}, target=${date})`);
        return isActive && dateMatches;
      });
      
      console.log('Active one-off slots found:', activeOneOffSlots);
      
      if (activeOneOffSlots.length > 0) {
        // Add all active one-off slots for this date
        availabilitySlots.push(...activeOneOffSlots.map(slot => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
          type: 'one-off'
        })));
        hasActiveOneOffForThisDate = true;
        console.log('Using one-off availability, ignoring recurring for this date');
      }
    }

    // If no active one-off availability for THIS specific date, use recurring availability
    if (!hasActiveOneOffForThisDate) {
      let recurringSlots = [];
      
      // Check for new weeklyAvailability format first
      if (provider.weeklyAvailability) {
        console.log('Using new weeklyAvailability format');
        recurringSlots = convertWeeklyToRecurring(provider.weeklyAvailability);
      } else if (provider.recurringAvailability) {
        console.log('Using legacy recurringAvailability format');
        recurringSlots = provider.recurringAvailability;
      }
      
      console.log('Checking recurring availability for dayOfWeek:', dayOfWeek);
      console.log('Available recurring slots:', recurringSlots);
      
      const matchingSlots = recurringSlots.filter(slot => {
        const isActive = slot.isActive;
        const dayMatches = slot.dayOfWeek === dayOfWeek;
        console.log(`Recurring slot check: isActive=${isActive}, dayMatches=${dayMatches} (slot.dayOfWeek=${slot.dayOfWeek}, target=${dayOfWeek})`);
        return isActive && dayMatches;
      });
      
      console.log('Matching recurring slots:', matchingSlots);
      
      availabilitySlots.push(...matchingSlots.map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
        type: 'recurring'
      })));
    }

    console.log('Final availability slots:', availabilitySlots);
    return availabilitySlots;
  };

  // Check if a time slot conflicts with existing bookings
  const isSlotBooked = (date, time, durationMinutes) => {
    const slotStart = new Date(`${date}T${time}:00`);
    const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);

    return existingBookings.some(booking => {
      const bookingStart = new Date(`${booking.scheduledDate}T${booking.scheduledTime}:00`);
      const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60000);
      
      // Check for overlap
      return (slotStart < bookingEnd && slotEnd > bookingStart);
    });
  };

  // Update available time slots when date changes
  useEffect(() => {
    if (!selectedDate) return;

    const updateAvailableSlots = async () => {
      setLoading(true);
      
      // Fetch existing bookings for the selected date
      const bookings = await fetchExistingBookings(selectedDate);
      setExistingBookings(bookings);

      // Get day of week (0 = Sunday, 6 = Saturday) - using timezone-safe parsing
      const [year, month, day] = selectedDate.split('-').map(Number);
      // Create date at noon local time to avoid timezone issues
      const dateObject = new Date(year, month - 1, day, 12, 0, 0);
      const dayOfWeek = dateObject.getDay();
      
      console.log(`Date parsing debug:`, {
        selectedDate,
        year, 
        month, 
        day,
        dateObject: dateObject.toString(),
        dayOfWeek,
        dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek]
      });
      
      // Debug: Log provider availability data
      console.log('Provider availability debug:', {
        selectedDate,
        dayOfWeek,
        oneOffAvailability: provider.oneOffAvailability,
        recurringAvailability: provider.recurringAvailability,
        weeklyAvailability: provider.weeklyAvailability
      });
      
      // Get provider availability for this date/day
      const availability = getProviderAvailability(selectedDate, dayOfWeek);
      
      console.log('Calculated availability:', availability);
      
      const allSlots = [];
      
      // Generate time slots for each availability period
      availability.forEach(period => {
        const slots = generateTimeSlots(period.startTime, period.endTime);
        allSlots.push(...slots);
      });

      console.log('All generated slots:', allSlots);

      // Get current time for filtering past slots
      const now = new Date();
      const today = new Date();
      const todayDateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const isToday = selectedDate === todayDateString;

      // Filter out booked slots and slots that don't have enough time for the session duration
      const availableSlots = allSlots.filter(slot => {
        // Check if slot is booked
        if (isSlotBooked(selectedDate, slot, duration)) {
          console.log('Slot is booked:', slot);
          return false;
        }

        // Check if slot is in the past (for today only)
        if (isToday) {
          // Parse the slot time and compare with current time
          const [slotHours, slotMinutes] = slot.split(':').map(Number);
          const slotDateTime = new Date();
          slotDateTime.setHours(slotHours, slotMinutes, 0, 0);
          
          // Add a small buffer (e.g., 30 minutes) so users can't book sessions starting immediately
          const bufferMinutes = 30;
          const minimumBookingTime = new Date(now.getTime() + bufferMinutes * 60000);
          
          if (slotDateTime <= minimumBookingTime) {
            console.log(`Slot is in the past or too soon: ${slot} (slot time: ${slotDateTime.toLocaleTimeString()}, minimum time: ${minimumBookingTime.toLocaleTimeString()})`);
            return false;
          }
        }

        // Check if there's enough consecutive time for the full session duration
        const slotStart = new Date(`${selectedDate}T${slot}:00`);
        const slotEnd = new Date(slotStart.getTime() + duration * 60000);
        
        // Find the availability period this slot belongs to
        const period = availability.find(p => {
          const periodStart = new Date(`${selectedDate}T${p.startTime}:00`);
          const periodEnd = new Date(`${selectedDate}T${p.endTime}:00`);
          return slotStart >= periodStart && slotEnd <= periodEnd;
        });

        if (!period) {
          console.log('No period found for slot:', slot, 'duration:', duration);
        }

        return !!period;
      });
      
      console.log('Final available slots:', availableSlots);
      setAvailableSlots(availableSlots.sort());
      setLoading(false);
    };

    updateAvailableSlots();
  }, [selectedDate, provider, duration]);

  // Format time for display (12-hour format)
  const formatTimeDisplay = (time24) => {
    const [hours, minutes] = time24.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  return (
    <div className="datetime-selector">
      {/* Date Selection */}
      <div className="date-selection">
        <h4>Select Date</h4>
        <div className="calendar-grid">
          {calendarDays.map(day => {
            const availability = getProviderAvailability(day.date, day.dayOfWeek);
            const hasAvailability = availability.length > 0;
            
            return (
              <button
                type="button"
                key={day.date}
                className={`calendar-day ${selectedDate === day.date ? 'selected' : ''} ${!hasAvailability ? 'unavailable' : ''} ${day.isToday ? 'today' : ''}`}
                onClick={() => {
                  console.log(`Calendar day clicked: ${day.date}, dayOfWeek: ${day.dayOfWeek}`);
                  if (hasAvailability) onDateChange(day.date);
                }}
                disabled={!hasAvailability}
                title={!hasAvailability ? 'Provider not available' : ''}
              >
                <div className="day-display">{day.display}</div>
                {day.isToday && <div className="today-label">Today</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div className="time-selection">
          <h4>Available Times</h4>
          {loading ? (
            <div className="loading-slots">Loading available times...</div>
          ) : availableSlots.length > 0 ? (
            <div className="time-slots-grid">
              {availableSlots.map(slot => (
                <button
                  type="button"
                  key={slot}
                  className={`time-slot ${selectedTime === slot ? 'selected' : ''}`}
                  onClick={() => onTimeChange(slot)}
                >
                  {formatTimeDisplay(slot)}
                </button>
              ))}
            </div>
          ) : (
            <div className="no-slots">
              No available time slots for this date. Please select another date.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateTimeSelector;